import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  CircularProgress,
  Grid,
  Divider,
  LinearProgress,
  Backdrop,
  useTheme,
} from "@mui/material";
import {
  FindInPage,
  Link as LinkIcon,
  DescriptionOutlined,
  CheckCircle,
  WarningAmber,
} from "@mui/icons-material";
import axios from "axios";
import API_BASE_URL from "../config";

interface PlagiarismDetail {
  sentence: string;
  source: string;
  similarity: number;
}

export default function PlagiarismChecker(): JSX.Element {
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [details, setDetails] = useState<PlagiarismDetail[]>([]);
  const [hasScanned, setHasScanned] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const handleScan = async () => {
    if (!inputText.trim()) return;
    setLoading(true);
    setHasScanned(false);
    try {
      const response = await axios.post(`${API_BASE_URL}/check-plagiarism`, {
        text: inputText,
      });
      if (response.data) {
        setScore(response.data.score);
        setDetails(response.data.details || []);
        setHasScanned(true);
      }
    } catch (error) {
      console.error("Plagiarism check failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (s: number) => {
    if (s < 5) return "#10b981"; // Green
    if (s < 20) return "#f59e0b"; // Amber
    return "#ef4444"; // Red
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", pt: 4 }}>
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 600,
            mb: 1,
            trackingTight: "-0.02em",
            color: isDarkMode ? "white" : "text.primary",
          }}
        >
          Plagiarism{" "}
          <span style={{ color: isDarkMode ? "#4b5563" : "#3b82f6" }}>
            Scan
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: isDarkMode ? "#9ca3af" : "text.secondary",
            fontWeight: 400,
            maxWidth: 600,
          }}
        >
          Scan millions of web pages and academic databases to ensure your work
          is 100% original.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
              border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              p: 3,
              minHeight: 500,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <TextField
                fullWidth
                multiline
                placeholder="Paste your text here to check for plagiarism..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontFamily: "inherit",
                    fontSize: "16px",
                    lineHeight: 1.7,
                    color: isDarkMode ? "white" : "text.primary",
                    alignItems: "flex-start",
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                mt: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#4b5563" : "text.secondary" }}
              >
                Supported: Multi-language scan
              </Typography>
              <Button
                variant="contained"
                onClick={handleScan}
                disabled={loading || !inputText.trim()}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <FindInPage />
                  )
                }
                sx={{
                  borderRadius: "12px",
                  bgcolor: isDarkMode ? "white" : "primary.main",
                  color: isDarkMode ? "black" : "white",
                  textTransform: "none",
                  px: 4,
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: isDarkMode ? "#e5e7eb" : "primary.dark",
                  },
                  "&.Mui-disabled": {
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)",
                    color: isDarkMode
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.25)",
                  },
                }}
              >
                Scan Text
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Stack spacing={3}>
            {/* Score Panel */}
            <Paper
              sx={{
                p: 4,
                borderRadius: "24px",
                bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "text.secondary",
                  mb: 3,
                  fontWeight: 500,
                }}
              >
                Originality Score
              </Typography>

              {score !== null ? (
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h2"
                    sx={{ fontWeight: 800, color: getScoreColor(score), mb: 1 }}
                  >
                    {100 - score}%
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDarkMode ? "white" : "text.primary",
                      fontWeight: 600,
                      mb: 3,
                    }}
                  >
                    {score === 0
                      ? "Purely Original"
                      : `${score}% Plagiarism Found`}
                  </Typography>

                  <Box sx={{ width: "100%", mb: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={100 - score}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: isDarkMode
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.06)",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: getScoreColor(score),
                        },
                      }}
                    />
                  </Box>
                </Box>
              ) : (
                <Box sx={{ py: 4, textAlign: "center", opacity: 0.3 }}>
                  <DescriptionOutlined
                    sx={{
                      fontSize: 48,
                      mb: 1,
                      color: isDarkMode ? "white" : "text.primary",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: isDarkMode ? "white" : "text.primary" }}
                  >
                    Scan results will appear here
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Found Sources */}
            {hasScanned && (
              <Paper
                sx={{
                  p: 3,
                  borderRadius: "24px",
                  bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                  border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                  maxHeight: 400,
                  overflowY: "auto",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: isDarkMode ? "white" : "text.primary",
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {score === 0 ? (
                    <CheckCircle sx={{ color: "#10b981", fontSize: 18 }} />
                  ) : (
                    <WarningAmber sx={{ color: "#ef4444", fontSize: 18 }} />
                  )}
                  {score === 0 ? "No Matches Found" : "Matched Sources"}
                </Typography>

                <Stack spacing={2}>
                  {details.length > 0
                    ? details.map((detail, i) => (
                        <Box
                          key={i}
                          sx={{
                            p: 2,
                            borderRadius: "12px",
                            bgcolor: isDarkMode
                              ? "rgba(255,255,255,0.02)"
                              : "rgba(0,0,0,0.02)",
                            border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"}`,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <LinkIcon sx={{ color: "#3b82f6", fontSize: 14 }} />
                            <Typography
                              variant="caption"
                              sx={{ color: "#3b82f6", fontWeight: 600 }}
                            >
                              {detail.source}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDarkMode ? "#9ca3af" : "text.secondary",
                              fontSize: "13px",
                              fontStyle: "italic",
                            }}
                          >
                            "{detail.sentence}"
                          </Typography>
                        </Box>
                      ))
                    : score === 0 && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDarkMode ? "#4b5563" : "text.secondary",
                            textAlign: "center",
                            py: 2,
                          }}
                        >
                          Great job! No plagiarism detected in our database.
                        </Typography>
                      )}
                </Stack>
              </Paper>
            )}
          </Stack>
        </Grid>
      </Grid>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: "column",
          gap: 2,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" sx={{ color: "white", fontWeight: 500 }}>
          Scanning databases...
        </Typography>
      </Backdrop>
    </Box>
  );
}
